if (!customElements.get('featured-prints')) {
    class FeaturedPrints extends HTMLElement {
        constructor() {
            super();
            this.track = this.querySelector('.fp-track');
            this.progressFill = this.querySelector('.js-fp-progress');

            // Nav buttons
            this.btnPrev = this.querySelector('.js-fp-prev');
            this.btnNext = this.querySelector('.js-fp-next');
            this.btnBotPrev = this.querySelector('.js-fp-bottom-prev');
            this.btnBotNext = this.querySelector('.js-fp-bottom-next');

            this.activeIndex = 0;
            this._rafId = null;

            this.init();
        }

        /* ─────────────────────────────────────────
           INIT
        ───────────────────────────────────────── */
        init() {
            if (!this.track) return;

            this.cards = Array.from(this.track.querySelectorAll('.fp-card'));
            if (this.cards.length === 0) return;

            // Wire nav buttons
            [this.btnPrev, this.btnBotPrev].filter(Boolean).forEach(btn =>
                btn.addEventListener('click', () => this._navigate('prev'))
            );
            [this.btnNext, this.btnBotNext].filter(Boolean).forEach(btn =>
                btn.addEventListener('click', () => this._navigate('next'))
            );

            // Add-to-cart feedback
            this.querySelectorAll('.fp-card form').forEach(form => {
                form.addEventListener('submit', (e) => {
                    const btn = form.querySelector('.fp-add-btn');
                    if (!btn) return;
                    const orig = btn.textContent;
                    btn.textContent = 'Adding…';
                    btn.disabled = true;
                    setTimeout(() => { btn.textContent = orig; btn.disabled = false; }, 2000);
                });
            });

            // rAF-throttled scroll → update progress + buttons
            this.track.addEventListener('scroll', () => {
                if (this._rafId) return;
                this._rafId = requestAnimationFrame(() => {
                    this._rafId = null;
                    this._onScroll();
                });
            }, { passive: true });

            // Touch swipe support
            this._initTouch();

            // Resize → re-anchor + update
            window.addEventListener('resize', () => {
                this._snapTo(this.activeIndex, false);
                this._updateProgress();
                this._updateButtons();
            });

            // Boot
            this._snapTo(0, false);
            this._updateProgress();
            this._updateButtons();
        }

        /* ─────────────────────────────────────────
           TOUCH – native swipe support
        ───────────────────────────────────────── */
        _initTouch() {
            let startX = 0;
            this.track.addEventListener('touchstart', (e) => {
                startX = e.touches[0].clientX;
            }, { passive: true });

            this.track.addEventListener('touchend', (e) => {
                const diff = startX - e.changedTouches[0].clientX;
                if (Math.abs(diff) > 40) {
                    this._navigate(diff > 0 ? 'next' : 'prev');
                }
            }, { passive: true });
        }

        /* ─────────────────────────────────────────
           NAVIGATE (button click)
        ───────────────────────────────────────── */
        _navigate(dir) {
            const n = this.cards.length;
            const next = this.activeIndex + (dir === 'next' ? 1 : -1);
            const clamped = Math.max(0, Math.min(n - 1, next));
            this._snapTo(clamped, true);
            this.activeIndex = clamped;
            this._syncUI();
        }

        /* ─────────────────────────────────────────
           SCROLL HANDLER
        ───────────────────────────────────────── */
        _onScroll() {
            this._syncActiveFromScroll();
            this._updateProgress();
            this._updateButtons();
        }

        _syncActiveFromScroll() {
            const sl = this.track.scrollLeft;
            let minD = Infinity, idx = 0;
            this.cards.forEach((card, i) => {
                const d = Math.abs((card.offsetLeft - this.track.offsetLeft) - sl);
                if (d < minD) { minD = d; idx = i; }
            });
            if (idx !== this.activeIndex) {
                this.activeIndex = idx;
                this._syncUI();
            }
        }

        /* ─────────────────────────────────────────
           ACTIVE STATE
        ───────────────────────────────────────── */
        _syncUI() {
            this.cards.forEach((card, i) =>
                card.classList.toggle('is-active-card', i === this.activeIndex)
            );
            this._updateButtons();
        }

        /* ─────────────────────────────────────────
           SNAP
        ───────────────────────────────────────── */
        _snapTo(idx, smooth) {
            const card = this.cards[idx];
            if (!card) return;
            const left = card.offsetLeft - this.track.offsetLeft;
            this.track.scrollTo({ left, behavior: smooth ? 'smooth' : 'instant' });
            this._updateProgress();
        }

        /* ─────────────────────────────────────────
           PROGRESS BAR
        ───────────────────────────────────────── */
        _updateProgress() {
            if (!this.progressFill || this.cards.length === 0) return;
            const first = this.cards[0];
            const last = this.cards[this.cards.length - 1];
            const start = first.offsetLeft - this.track.offsetLeft;
            const end = (last.offsetLeft - this.track.offsetLeft) + last.offsetWidth - this.track.clientWidth;
            const range = end - start;
            const raw = range > 0 ? (this.track.scrollLeft - start) / range : 0;
            const pct = Math.min(100, Math.max(0, raw * 100));
            this.progressFill.style.width = pct + '%';
        }

        /* ─────────────────────────────────────────
           BUTTON STATE (disable at edges)
        ───────────────────────────────────────── */
        _updateButtons() {
            const atStart = this.activeIndex === 0;
            const atEnd = this.activeIndex === this.cards.length - 1;

            [this.btnPrev, this.btnBotPrev].filter(Boolean).forEach(btn => {
                btn.disabled = atStart;
            });
            [this.btnNext, this.btnBotNext].filter(Boolean).forEach(btn => {
                btn.disabled = atEnd;
            });
        }
    }

    customElements.define('featured-prints', FeaturedPrints);
}
